import { getFormattedDate, getUTCDate } from '../hooks/useFormatDate';
import { windowService } from './window.utils';

const addLeadingZeros = (num: number, totalLength: number): string => String(num).padStart(totalLength, '0');
const formatComponentNumber = (num: number): string => addLeadingZeros(num, 2);
export const getDLMFormatDate = (dateString: string) => {
  const utcDate = getUTCDate(dateString);
  const DLM_DATE_FORMAT = 'yyyyMMdd';
  return getFormattedDate(utcDate, DLM_DATE_FORMAT);
};

function googleDataLayerPush(dataLayerData: any): void {
  windowService().dataLayer = windowService()?.dataLayer || [];

  if (!windowService().dataLayer) {
    return;
  }

  windowService().dataLayer.push(dataLayerData);
}

function googleClickEvent(
  type: string,
  category: string,
  action: string | undefined,
  label: string | undefined,
  elementNumber?: number | undefined,
  news_type?: string | undefined,
  date?: string | undefined,
  event_type?: string | undefined,
  event_status?: string | undefined
): void {
  action = action ?? '';
  label = label ?? '';

  const dataLayerData = {
    event: 'click',
    _clear: true,
    'data-tag-meta': {
      type,
      category,
      action,
      label,
      ...(news_type && { news_type }),
      ...(date && { date }),
      ...(event_type && { event_type }),
      ...(event_status && { event_status }),
    },
  };

  if (typeof elementNumber !== 'undefined') {
    dataLayerData['data-tag-meta']['elementNo'] = formatComponentNumber(elementNumber);
  }

  googleDataLayerPush(dataLayerData);
}

function googleFormSubmitEvent(formName: string | undefined, fileUploaded: boolean | undefined): void {
  const dataLayerData = {
    event: 'click',
    'data-tag-meta': {
      type: 'form',
      category: 'Form Submit',
      action: 'Success',
      label: formName,
      fileUpload: fileUploaded ? 'Yes' : 'No',
    },
  };

  googleDataLayerPush(dataLayerData);
}

function googleClickCtaEvent(
  category: 'Button' | 'Link' | 'Hero' | 'Page Nav' | 'Global Nav' | 'Global Footer' | 'Brochure' | 'Convert to Excel' | 'Print' | 'News Files',
  action: string | undefined,
  label: string | undefined,
  elementNumber?: number | undefined
) {
  googleClickEvent('cta', category, action, label, elementNumber);
}

function googleClickNewsCtaEvent(label: string | undefined, elementNumber?: number, news_type?: string, date?: string) {
  googleClickEvent('cta', 'Button', 'News Click', label, elementNumber, news_type, date);
}

function googleClickEventsCtaEvent(
  label: string | undefined,
  elementNumber?: number,
  event_type?: string,
  date?: string,
  event_status?: string
) {
  googleClickEvent('cta', 'Button', 'Events Click', label, elementNumber, undefined, date, event_type, event_status);
}

function googleClickNavEvent(
  category: 'Page Nav' | 'Page Sub Nav' | 'Global Footer' | 'Global Nav',
  action: string | undefined,
  label: string | undefined,
  elementNumber?: number | undefined
) {
  googleClickEvent('nav', category, action, label, elementNumber);
}

function googleClickSubNavigationEvent(action: string | undefined, label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('nav', 'Page Sub Nav', action, label, elementNumber);
}

function googleClickSlideEvent(action: string | undefined, label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('slide', 'Carousel', action, label, elementNumber);
}

function googleClickOpenVideoEvent(label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('video', 'Video', 'Open', label, elementNumber);
}

function googleClickAccordionEvent(action: string | undefined, label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('accordion', 'Accordion', action, label, elementNumber);
}

function googleClickCardEvent(label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('card', 'Card', 'Click', label, elementNumber);
}

function googleSeeMoreCardEvent(label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('card', 'Link', 'See More', label, elementNumber);
}

function googleClickBreadcrumbEvent(label: string | undefined): void {
  googleClickEvent('breadcrumb', 'Breadcrumb', 'Click', label);
}

function googleClickDropdownEvent(action: string | undefined, label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('dropdown', 'Dropdown', action, label, elementNumber);
}

function googleClickTabEvent(action: 'Open' | 'Select', label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('tab', 'Tab', action, label, elementNumber);
}

function googleSocialEvent(label: string | undefined, elementNumber?: number | undefined): void {
  googleClickEvent('social', 'Social', 'Share', label, elementNumber);
}

function googleClickSearchEvent(action: string | undefined, label: string | undefined): void {
  googleClickEvent('search', 'Search Result', action, label);
}

function googleClickRichTextEvent(event: any, label: string | undefined, elementNumber?: number | undefined): void {
  const anchorElement = event?.target;

  if (!anchorElement || anchorElement.nodeName !== 'A') {
    return;
  }

  const anchorText = anchorElement.text;
  googleClickCtaEvent('Link', anchorText, label, elementNumber);
}

function googleClickDealerLocatorResults(
  event: string,
  dealerLocatorCountry: any | undefined,
  dealerLocatorSearch: string | undefined,
  dealerLocatorEquipment: string | undefined,
  dealerLocatorServices: string | undefined,
  dealerLocatorType: string | undefined,
  dealerLocatorState: string | undefined
): void {
  const dataLayerData = {
    event,
    dealerLocatorCountry,
    dealerLocatorSearch,
    dealerLocatorEquipment,
    dealerLocatorServices,
    dealerLocatorType,
    dealerLocatorState,
  };

  googleDataLayerPush(dataLayerData);
}

function googleGeolocationClick(
  event: string,
  region: string | undefined,
  language: string | undefined,
  market: string | undefined,
  dealerLocatorType: string | undefined
): void {
  const dataLayerData = {
    event,
    region,
    language,
    market,
    dealerLocatorType,
  };

  googleDataLayerPush(dataLayerData);
}

function googleClickDealerLocatorGetDetals(
  event: string,
  dealerLocatorCountry: string | undefined,
  dealerLocatorSearch: string | undefined,
  dealerName: string | undefined,
  certifiedDealer: string | undefined,
  dealerLocatorType: string | undefined
): void {
  const dataLayerData = {
    event,
    dealerLocatorCountry,
    dealerLocatorSearch,
    dealerName,
    certifiedDealer,
    dealerLocatorType,
  };

  googleDataLayerPush(dataLayerData);
}

function googleDealerLocatorContactClick(
  event: string,
  type: string | undefined,
  dealerName: string | undefined,
  dealerLocatorType: string | undefined
): void {
  const dataLayerData = {
    event,
    type,
    dealerName,
    dealerLocatorType,
  };

  googleDataLayerPush(dataLayerData);
}

function googleFormProgress(form_name, form_step: string, form_step_name: string | undefined) {
  const dataLayerData = {
    event: 'form_progress',
    form_name,
    form_step,
    form_step_name,
  };
  googleDataLayerPush(dataLayerData);
}

function googleDownloadBrochureListingEvent(
  event: string,
  productLine?: string,
  productSeries?: string,
  productCategory?: string,
  search_term?: string
) {
  const dataLayerData = {
    event,
    productLine,
    productSeries,
    productCategory,
    search_term,
  };
  googleDataLayerPush(dataLayerData);
}

export {
  formatComponentNumber,
  googleDataLayerPush,
  googleFormSubmitEvent,
  googleClickCtaEvent,
  googleClickNewsCtaEvent,
  googleClickSlideEvent,
  googleClickOpenVideoEvent,
  googleClickAccordionEvent,
  googleClickCardEvent,
  googleSeeMoreCardEvent,
  googleClickBreadcrumbEvent,
  googleClickDropdownEvent,
  googleClickNavEvent,
  googleClickSubNavigationEvent,
  googleClickTabEvent,
  googleSocialEvent,
  googleClickSearchEvent,
  googleClickRichTextEvent,
  googleClickEvent,
  googleClickDealerLocatorResults,
  googleGeolocationClick,
  googleClickDealerLocatorGetDetals,
  googleDealerLocatorContactClick,
  googleClickEventsCtaEvent,
  googleFormProgress,
  googleDownloadBrochureListingEvent,
};
